/**
 * Shape + predicates for the advanced-filter panel's filter state.
 *
 * Split out of `AdvancedFilterPanel.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/AdvancedFilterPanel.normalization.test.tsx`.
 *
 * The `any`s these declarations replace were load-bearing only in the sense
 * that nobody had typed them: a filter VALUE is genuinely open (a string, a
 * string[], a {min,max} range, a boolean), which is `unknown`, not `any`.
 * `unknown` still flows into the filter-input children that declare `any`.
 */

/** One field's filter as the panel displays it. */
export interface DisplayFilter {
  type: string;
  value: unknown;
}

/** `undefined` when the field carries no filter at all. */
export type CurrentFilter = DisplayFilter | undefined;

/** One entry of the array (`AdvancedFilterGroup[]`) shape of the prop. */
export interface AdvancedFilterEntry {
  id: string;
  field: string;
  operator: string;
  value: unknown;
}

/**
 * Either shape the panel accepts for `advancedFilters`.
 *
 * The record half stays `Record<string, unknown>` to match what
 * `QuickFiltersAdvancedProps` (the only caller) declares — narrowing it to
 * `Record<string, DisplayFilter>` would break that untouched module's compile.
 */
export type AdvancedFiltersProp = Record<string, unknown> | AdvancedFilterEntry[];

/**
 * Normalize advanced filters from array (`AdvancedFilterGroup[]`) or Record to
 * Record format for UI display. When array, takes the LAST filter per field
 * (the UI models one filter per field).
 *
 * A field carrying more than one condition therefore loses all but the last
 * HERE — which is why `collapsedFilterFields` exists below and why the panel
 * shows a notice naming them. Never let this collapse happen silently.
 */
export function normalizeAdvancedFiltersForDisplay(
  advancedFilters: AdvancedFiltersProp
): Record<string, DisplayFilter> {
  if (Array.isArray(advancedFilters)) {
    const result: Record<string, DisplayFilter> = {};
    advancedFilters.forEach((f) => {
      if (f && f.value !== null && f.value !== undefined && f.value !== "") {
        // `type` is not on AdvancedFilterEntry, but callers have historically
        // passed either spelling — keep reading both.
        const legacyType = (f as { type?: unknown }).type;
        const operator =
          f.operator || (typeof legacyType === "string" ? legacyType : undefined) || "eq";
        result[f.field || f.id] = { type: operator, value: f.value };
      }
    });
    return result;
  }
  // Record branch: entries are produced by the filter machinery already shaped
  // as `{ type, value }`. Narrow rather than validate — a guard that DROPPED an
  // unrecognised entry would silently lose a user's filter, which the pre-split
  // code never did.
  return advancedFilters as Record<string, DisplayFilter>;
}

/**
 * Field ids the array shape carries MORE THAN ONE condition for.
 *
 * `normalizeAdvancedFiltersForDisplay` can only keep one — the panel's whole
 * mutation API is per-field (`removeAdvancedFilter(fieldId)`), so a second
 * condition on the same field is unrepresentable in this UI. Collapsing it
 * silently was the bug: the field's input showed the LAST condition while the
 * first one was still being applied to the data, and nothing on screen said so.
 * The panel calls this to say it out loud instead.
 *
 * Only entries that survive the same empty-value filter as the normalizer count,
 * so a dropped null/"" condition never raises a phantom conflict.
 */
export function collapsedFilterFields(advancedFilters: AdvancedFiltersProp): string[] {
  if (!Array.isArray(advancedFilters)) return [];
  const counts = new Map<string, number>();
  advancedFilters.forEach((f) => {
    if (!f || f.value === null || f.value === undefined || f.value === "") return;
    const fieldId = f.field || f.id;
    counts.set(fieldId, (counts.get(fieldId) ?? 0) + 1);
  });
  return [...counts.entries()].filter(([, count]) => count > 1).map(([fieldId]) => fieldId);
}

/**
 * Whether a field's current filter carries a meaningful (non-empty) value.
 *
 * Note `false` and `0` DO count — only null / undefined / "" / an empty array /
 * an object whose every value is empty are treated as "no filter".
 */
export function filterHasValue(filter: CurrentFilter): boolean {
  if (!filter) return false;
  const { value } = filter;
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.values(value).some((v: unknown) => v !== null && v !== undefined && v !== "");
  }
  return true;
}
